// ═══════════════════════════════════════════════════════════════════════
// TryOnModal.js — Modal "Essayer cette tenue"
// ═══════════════════════════════════════════════════════════════════════
//
// v1.10.0 : Workflow en 4 étapes
// v1.10.3 : Ajout étape "pieces" — sélection des pièces à essayer
//
// Workflow :
//   1. PIECES    : choix des pièces à inclure (cochées par défaut)
//   2. MANNEQUIN : choix du mannequin (14 disponibles, filtrés par genre)
//   3. LOADING   : appels FASHN séquentiels (15-30s)
//   4. RESULT    : image générée + actions (sauver, partager, ré-essayer, voir articles)
//   5. QUOTA     : si quota atteint → CTA upgrade
//
// FASHN ne supporte bien que les pièces "structurelles" (Robes, Hauts, Bas,
// Vestes, Chaussures). Les bijoux et accessoires sont automatiquement
// EXCLUS de la sélection — pas affichés ici, mais visibles dans le
// carrousel "Articles de cette tenue" du résultat.
//
// Cap à 4 pièces max (contrainte FASHN + contrôle coûts : 4 × $0.075 = $0.30)
// ═══════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, Image, ActivityIndicator, Share, Alert, Dimensions, useWindowDimensions, Platform,
  DeviceEventEmitter,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import AppModal from './AppModal';
import { showAppAlert } from './AppDialog';
// v1.10.86 : useSafeAreaInsets pour padding-top dynamique sur iOS dynamic island
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  getMannequinsByGender,
  performTryOn,
  fetchTryOnUsage,
  TRYON_PROVIDERS,
  DEFAULT_PROVIDER,
  getProviderById,
  // v1.12.175 : photo perso comme mannequin Vertex VTO
  fetchPersonalMannequin,
  requestPersonalMannequinUpload,
  uploadPersonalMannequinFile,
  confirmPersonalMannequin,
  deletePersonalMannequin,
  buildPersonalMannequinEntry,
} from '../services/TryOnService';
import { optimizeImageForAI } from '../services/ImageOptimizer';
import { useI18n } from '../i18n';
import { isAdultForPersonalTryOn, MIN_AGE_PERSONAL_TRYON } from '../services/AgeVerificationService';
import ReportContentModal from './ReportContentModal';
import { openLegalUrl } from '../utils/openLegalUrl';
import { recordConsent } from '../services/ConsentService';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const C = {
  bg: '#FAF8F4',
  card: '#FFFFFF',
  text: '#1A1A1A',
  textMuted: '#6B6B6B',
  textFaint: '#A0A0A0',
  border: '#EAE6DF',
  gold: '#D4AF72',
  goldDark: '#B8860B',
  red: '#E53935',
  green: '#4CAF50',
};

// Catégories portables par FASHN (essayage virtuel)
// v1.12.204 — 'Manteaux' ajouté (porté sur Vestes pour layering hivernal)
const WEARABLE_CATEGORIES = ['Robes', 'Hauts', 'Bas', 'Vestes', 'Manteaux', 'Chaussures'];

// v1.12.532 — Force HTTPS sur les URLs http:// (Mixed Content sur web HTTPS)
// Les articles TOUS du dressing ont parfois encore des photoUrl http://
const safeUri = (uri) => {
  if (!uri || typeof uri !== 'string') return uri;
  return uri.startsWith('http://') ? 'https://' + uri.slice(7) : uri;
};
// Cap à 4 pièces max (contrainte FASHN + coûts)
const MAX_GARMENTS = 4;

export default function TryOnModal({
  visible,
  onClose,
  outfit,
  userGender = 'F',
  authToken,
  onUpgradePress,
  onProductPress,
}) {
  const { t, lang } = useI18n();
  // v1.12.533 — Lightbox plein écran pour zoomer sur le résultat (pinch-to-zoom iOS)
  const [zoomVisible, setZoomVisible] = useState(false);
  // v1.12.625 — Callback stable pour onRequestClose AppModal zoom (sinon le
  // cleanup history.back propage le popstate à l'outer TryOnModal → ferme tout).
  // Voir memoire [[appmodal-callback-stable]].
  const closeZoom = useCallback(() => setZoomVisible(false), []);
  // v1.12.530 — Responsive : sur web large, limiter la taille des mannequins/résultat
  const { width: winW, height: winH } = useWindowDimensions();
  const isWeb = Platform.OS === 'web';
  // Largeur "mobile équivalente" pour le grid mannequins (max 380px sur web)
  const contentW = isWeb ? Math.min(winW, 380) : winW;
  const mannequinCardW = (contentW - 32 - 12) / 2;
  // Résultat : max 320px de large sur web, full sur mobile
  const resultMaxW = isWeb ? Math.min(winW * 0.6, 360) : null;
  const [step, setStep] = useState('pieces');
  const [selectedItemKeys, setSelectedItemKeys] = useState(new Set());
  const [selectedMannequin, setSelectedMannequin] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(DEFAULT_PROVIDER); // v1.12.6
  const [resultUrl, setResultUrl] = useState(null);
  const [resultProvider, setResultProvider] = useState(null);                  // v1.12.6
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState({ count: 0, limit: 0, tier: 'free', nextResetDate: null, isPaidTier: false });
  // v1.12.490 — état dédié : 'pending' au mount, 'allowed' si quota OK, 'blocked' si limit
  // atteinte. Si 'blocked' → return null AVANT de render AppModal (évite l'animation iOS
  // natuive slide + conflit Animated listeners → SIGABRT).
  const [_limitState, _setLimitState] = useState('pending');
  // v1.10.6 : Stage de progression du polling async
  const [loadingStage, setLoadingStage] = useState('pending');
  // v1.10.86 : safe area insets pour le header (iOS dynamic island + Android)
  const insets = useSafeAreaInsets();
  // v1.12.175 : photo perso de l'user (chargée au montage)
  const [personalMannequin, setPersonalMannequin] = useState(null); // { viewUrl, uploadedAt } | null
  // v1.12.940 — Apple 1.1 : consentement 3 points pour le Try-On « On you ».
  const [consent18,      setConsent18]      = useState(false); // j'ai 18 ans ou plus
  const [consentSelf,    setConsentSelf]    = useState(false); // c'est une photo de moi
  const [consentNoMinor, setConsentNoMinor] = useState(false); // aucun mineur sur la photo
  const consentChecked = consent18 && consentSelf && consentNoMinor;
  const resetConsent = useCallback(() => { setConsent18(false); setConsentSelf(false); setConsentNoMinor(false); }, []);
  const [showReport, setShowReport] = useState(false); // v1.12.940 — modal signalement

  // ─── Articles portables de la tenue (filtrés par WEARABLE_CATEGORIES) ───
  // v1.12.580 — V5.1-bis : exclure les items _hidden (au-delà du quota tier+bonus).
  // L'user doit upgrade / Boost / abonnement wardrobe pour pouvoir les utiliser en try-on.
  const wearableItems = useMemo(() => {
    if (!outfit?.items) return [];
    return outfit.items.filter(it => it && WEARABLE_CATEGORIES.includes(it.category) && it.image && !it._hidden);
  }, [outfit]);

  // ─── Articles non-portables (bijoux, accessoires) — affichés en info ───
  const nonWearableItems = useMemo(() => {
    if (!outfit?.items) return [];
    return outfit.items.filter(it => it && !WEARABLE_CATEGORIES.includes(it.category) && !it._hidden);
  }, [outfit]);

  // ─── Helper : clé unique pour un item ───
  const itemKey = (item, idx) => item.id || item.productKey || `${item.category}-${idx}`;

  // ─── Helper : traduction catégorie FR (DB) → langue courante ───
  const tCat = useCallback((category) => {
    if (!category) return '';
    const map = {
      Robes: 'cat_dresses',
      Hauts: 'cat_tops',
      Vestes: 'cat_jackets',
      Manteaux: 'cat_jackets', // fallback (pas de cat_coats dédié)
      Bas: 'cat_bottoms',
      Chaussures: 'cat_shoes',
      Sacs: 'cat_bags',
      Accessoires: 'cat_accessories',
      Bijoux: 'cat_jewelry',
    };
    const k = map[category];
    return k ? t(k) : category;
  }, [t]);

  // ─── Helper : traduction bodyType / silhouette ───
  const tBody = useCallback((bodyType) => {
    if (!bodyType) return '';
    const map = {
      'mince': 'mannequin_slim',
      'athlétique': 'mannequin_athletic',
      'curvy': 'mannequin_curvy',
      'tall': 'mannequin_tall',
      'petite': 'mannequin_petite',
      'plus size': 'mannequin_plus_size',
      'votre silhouette': 'your_silhouette',
    };
    const k = map[bodyType];
    return k ? t(k) : bodyType;
  }, [t]);

  // ─── Helper : traduction "hair" (blonde / brune) ───
  const tHair = useCallback((hair) => {
    if (!hair) return '';
    const map = { blonde: 'hair_blonde', brune: 'hair_brunette' };
    const k = map[hair];
    return k ? t(k) : hair;
  }, [t]);

  // ─── Reset à l'ouverture : pré-sélectionner toutes les pièces portables (cap 4) ───
  useEffect(() => {
    if (visible) {
      setStep('pieces');
      setSelectedMannequin(null);
      setResultUrl(null);
      setResultProvider(null);
      setError(null);
      // v1.12.6 : on ne reset PAS selectedProvider — l'utilisateur garde
      // son choix entre 2 essayages successifs (utile pour comparer FASHN
      // vs Vertex sur la même tenue avec différents mannequins).
      // v1.12.768 — Pré-cocher TOUS les wearables de la tenue (cap MAX_GARMENTS)
      // pour que Vertex VTO essaie l'outfit complet. Priorité si > MAX :
      //   Robes > Hauts > Bas > Chaussures > Vestes > Manteaux
      // Avant : Vestes/Manteaux décochées par défaut → user voyait le mannequin
      // sans veste alors qu'il avait sélectionné un look avec veste.
      const CAT_PRIORITY = { 'Robes': 1, 'Hauts': 2, 'Bas': 3, 'Chaussures': 4, 'Vestes': 5, 'Manteaux': 6 };
      const sorted = [...wearableItems]
        .map((it, idx) => ({ it, idx, prio: CAT_PRIORITY[it.category] ?? 99 }))
        .sort((a, b) => a.prio - b.prio);
      const finalKeys = new Set();
      for (const { it, idx } of sorted) {
        if (finalKeys.size >= MAX_GARMENTS) break;
        finalKeys.add(itemKey(it, idx));
      }
      setSelectedItemKeys(finalKeys);
    }
  }, [visible, outfit?.id, wearableItems.length]);

  // ─── v1.12.175 : Charger la photo perso au montage ───
  useEffect(() => {
    if (!visible || !authToken) return;
    let cancelled = false;
    fetchPersonalMannequin(authToken).then(d => {
      if (cancelled) return;
      if (d?.hasPersonalMannequin) {
        setPersonalMannequin({ viewUrl: d.viewUrl, uploadedAt: d.uploadedAt });
      } else {
        setPersonalMannequin(null);
      }
    }).catch(e => console.warn('[TryOn] fetchPersonalMannequin:', e.message));
    return () => { cancelled = true; };
  }, [visible, authToken]);

  // ─── Récupérer l'usage au montage ───
  useEffect(() => {
    if (!visible || !authToken) {
      // v1.12.490 — Reset state quand le modal se ferme (pour permettre la
      // prochaine ouverture sans rester bloqué sur 'blocked').
      if (!visible) _setLimitState('pending');
      return;
    }
    let cancelled = false;
    fetchTryOnUsage(authToken).then(u => {
      if (!cancelled) {
        setUsage(u);
        // v1.12.196 (rollback v1.12.194) : si quota atteint OU free, on ferme
        // et on émet l'event pour ouvrir la popup upgrade (PremiumGuard).
        if (u && (u.limit === 0 || u.count >= u.limit)) {
          // v1.12.490 — Fix crash SIGABRT iOS définitif :
          // 1. setLimitState('blocked') → render retourne null (AppModal jamais monté)
          // 2. Pas d'animation iOS native trigger → pas de conflit Animated listeners
          // 3. Émet l'event tryon_limit_reached pour que PremiumGuard affiche PremiumModal
          // 4. Parent peut setVisible(false) tranquillement (rien à unmount côté natif)
          _setLimitState('blocked');
          DeviceEventEmitter.emit('tryon_limit_reached', {
            tier:        u.tier,
            limit:       u.limit,
            current:     u.count,
            isPaidTier:  u.isPaidTier,
            nextResetDate: u.nextResetDate,
          });
          // onClose direct mais sans animation natuive en cours (return null avant render)
          onClose?.();
        } else {
          _setLimitState('allowed');
        }
      }
    });
    return () => { cancelled = true; };
  }, [visible, authToken]);

  // ─── Toggle sélection d'une pièce ───
  const toggleItem = useCallback((key) => {
    setSelectedItemKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // Hard cap à MAX_GARMENTS
        if (next.size >= MAX_GARMENTS) {
          Alert.alert(
            t('tryon_max_reached_title'),
            t('tryon_max_reached_msg').replace('{max}', String(MAX_GARMENTS))
          );
          return prev;
        }
        next.add(key);
      }
      return next;
    });
  }, []);

  // ─── Passer à l'étape mannequin (ou capture si première fois) ───
  const goToMannequinStep = useCallback(() => {
    if (selectedItemKeys.size === 0) {
      Alert.alert(t('tryon_no_piece_title'), t('tryon_no_piece_msg'));
      return;
    }
    setError(null);
    if (!personalMannequin) {
      // v1.12.940 — Apple 1.1 : Try-On « On you » (photo perso) réservé aux 18+.
      if (!isAdultForPersonalTryOn()) {
        Alert.alert(
          lang === 'en' ? 'Reserved for ages 18+' : 'Réservé aux 18 ans et plus',
          lang === 'en'
            ? `Personal Try-On (on your own photo) requires you to be ${MIN_AGE_PERSONAL_TRYON}+ years old. You can still use the mannequin Try-On.`
            : `Le Try-On sur votre propre photo est réservé aux personnes de ${MIN_AGE_PERSONAL_TRYON} ans et plus. Vous pouvez toujours utiliser le Try-On sur mannequin.`,
        );
        setStep('mannequin');
        return;
      }
      setStep('capture-intro');
    } else {
      setStep('mannequin');
    }
  }, [selectedItemKeys, personalMannequin, lang]);

  // ─── v1.12.184 : actions sur "Mon mannequin" depuis la liste try-on ───
  // Bouton ⋯ sur la carte → menu Supprimer / Remplacer.
  // v1.12.237 — limitation 7 jours retirée (handler.js v1.12.220) → on peut
  // toujours remplacer ou supprimer la photo, sans avertissement.
  const handlePersonalMannequinAction = useCallback(() => {
    showAppAlert(
      t('my_mannequin'),
      t('personal_mannequin_action_msg'),
      [
        { text: t('cancel'), style: 'cancel' },
        {
          text: t('replace'),
          onPress: async () => {
            try {
              await deletePersonalMannequin(authToken);
              setPersonalMannequin(null);
              resetConsent();
              setError(null);
              setStep('capture-intro');
            } catch (e) {
              setError(`${t('failed_label')}: ${e.message}`);
            }
          },
        },
        {
          text: t('delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await deletePersonalMannequin(authToken);
              setPersonalMannequin(null);
              setError(null);
            } catch (e) {
              setError(`${t('failed_label')}: ${e.message}`);
            }
          },
        },
      ],
    );
  }, [authToken, personalMannequin]);

  // ─── v1.12.175 : capture + upload photo perso ───
  const handleCaptureMannequin = useCallback(async (useCamera) => {
    if (!consentChecked) {
      Alert.alert(t('consent_required_title'), t('consent_required_msg'));
      return;
    }
    // v1.12.943 — Apple 1.1 : enregistre le consentement 18+ en BD (audit trail).
    // Fire-and-forget : n'interrompt pas le flux si l'appel échoue.
    recordConsent({
      authToken,
      consentType: 'personal_tryon_18plus',
      metadata: { age18: consent18, isSelf: consentSelf, noMinor: consentNoMinor, useCamera },
    });
    try {
      setError(null);
      let result;
      if (useCamera) {
        const perm = await ImagePicker.requestCameraPermissionsAsync();
        if (perm.status !== 'granted') {
          Alert.alert(t('permission_required'), t('permission_camera_required'));
          return;
        }
        result = await ImagePicker.launchCameraAsync({
          mediaTypes: ['images'], quality: 1.0, allowsEditing: false, exif: false,
        });
      } else {
        const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (perm.status !== 'granted') {
          Alert.alert(t('permission_required'), t('permission_gallery_required'));
          return;
        }
        result = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ['images'], quality: 1.0, allowsEditing: false, exif: false,
        });
      }
      if (result.canceled || !result.assets?.[0]) return;

      let fileUri = result.assets[0].uri;
      try {
        const opt = await optimizeImageForAI(fileUri);
        if (opt?.uri) fileUri = opt.uri;
      } catch (eOpt) {
        console.warn('[TryOn] image optim échoué :', eOpt.message);
      }

      setStep('uploading');
      const upload = await requestPersonalMannequinUpload({ authToken, contentType: 'image/jpeg' });
      await uploadPersonalMannequinFile({
        uploadUrl: upload.uploadUrl,
        fileUri,
        contentType: upload.contentType || 'image/jpeg',
      });
      await confirmPersonalMannequin({ authToken, s3Key: upload.s3Key });
      const refreshed = await fetchPersonalMannequin(authToken);
      if (refreshed?.hasPersonalMannequin) {
        setPersonalMannequin({ viewUrl: refreshed.viewUrl, uploadedAt: refreshed.uploadedAt });
      }
      setStep('mannequin');
    } catch (e) {
      console.warn('[TryOn] capture mannequin failed:', e.message);
      // v1.12.237 — rate-limit 7 jours retiré côté Lambda (handler v1.12.220) :
      // si on reçoit encore le code (ex : OTA pas à jour côté backend), on
      // affiche un message générique sans mentionner la limitation.
      setError(`${t('upload_failed_label')}: ${e.message}`);
      setStep('capture-intro');
    }
  }, [consentChecked, authToken]);

  // ─── Lancer le try-on ───
  const handleTryOn = useCallback(async (mannequin) => {
    setSelectedMannequin(mannequin);
    setStep('loading');
    setLoadingStage('pending');
    setError(null);

    try {
      // Récupérer les items sélectionnés
      const wearables = wearableItems
        .filter((it, idx) => selectedItemKeys.has(itemKey(it, idx)))
        .slice(0, MAX_GARMENTS);

      if (wearables.length === 0) {
        throw new Error(t('tryon_no_piece_selected'));
      }

      const garmentImages = wearables.map(it => it.image);
      const result = await performTryOn({
        mannequinId: mannequin.id,
        garmentImages,
        authToken,
        provider: selectedProvider, // v1.12.6
        onProgress: (stage) => setLoadingStage(stage),
      });

      setResultUrl(result.imageUrl);
      setResultProvider(result.provider || selectedProvider); // v1.12.6
      setUsage(prev => ({ ...prev, count: result.usage || prev.count + 1 }));
      setStep('result');

      // v1.12.731 — Persister le résultat try-on pour cette tenue dans
      // tryon_outfit_results → OutfitCard switch Articles/Flatlay/Try-on
      try {
        const { saveResult } = require('../services/TryOnResultsService');
        if (outfit && result?.imageUrl) {
          saveResult({ outfit, kind: 'tryon', imageUrl: result.imageUrl }).catch(() => {});
        }
      } catch {}
    } catch (e) {
      if (e.code === 'QUOTA_EXCEEDED') {
        setUsage({
          count: e.usage || usage.count,
          limit: e.limit || usage.limit,
          tier: e.tier || usage.tier,
          nextResetDate: e.nextResetDate || usage.nextResetDate,
          isPaidTier: e.isPaidTier !== undefined ? e.isPaidTier : usage.isPaidTier,
        });
        setStep('quota');
        return;
      }
      console.warn('[TryOn] error:', e.message);
      setError(e.message || t('tryon_generic_error'));
      setStep('mannequin');
    }
  }, [wearableItems, selectedItemKeys, authToken, usage.count, usage.limit, usage.tier]);

  // ─── Partager le résultat ───
  const handleShare = useCallback(async () => {
    if (!resultUrl) return;
    try {
      await Share.share({
        message: `${t('share_outfit_message')} ${resultUrl}`,
        url: resultUrl,
      });
    } catch (e) {
      console.warn('[TryOn] share failed:', e.message);
    }
  }, [resultUrl]);

  const filteredMannequins = useMemo(() => {
    const stock = getMannequinsByGender(userGender);
    if (personalMannequin?.viewUrl) {
      return [buildPersonalMannequinEntry({ viewUrl: personalMannequin.viewUrl }), ...stock];
    }
    return stock;
  }, [userGender, personalMannequin]);

  // v1.12.490 — Si quota dépassé, return null AVANT de render AppModal.
  // Empêche le Modal natif iOS de slide-in puis slide-out (cause du crash SIGABRT).
  if (_limitState === 'blocked') return null;

  return (
    <>
    {/* v1.12.940 — on masque ce modal quand le report s'ouvre (iOS n'empile pas 2 RNModal) */}
    <AppModal visible={visible && !showReport} animationType="slide" presentationStyle="overFullScreen" onRequestClose={onClose}>
      {/* v1.10.86 : View avec padding-top safe area (overFullScreen ignore SafeAreaView).
          insets.top ≈ 59px sur iPhone dynamic island, ≈ 24px sur Android. Fallback 20px. */}
      <View style={{ flex: 1, backgroundColor: C.bg, paddingTop: Math.max(insets.top, 20) }}>
        {/* Header */}
        <View style={{
          flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
          padding: 16, borderBottomWidth: 1, borderBottomColor: C.border,
        }}>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 16, fontWeight: '700', color: C.text }}>
              {step === 'pieces'        && `👗 ${t('tryon_choose_pieces')}`}
              {step === 'capture-intro' && `📸 ${t('tryon_create_my_mannequin')}`}
              {step === 'uploading'     && `⏳ ${t('tryon_upload_in_progress')}`}
              {step === 'mannequin'     && `✨ ${t('tryon_choose_mannequin')}`}
              {step === 'loading'       && `⏳ ${t('tryon_generating_in_progress')}`}
              {step === 'result'        && `🎉 ${t('tryon_result')}`}
              {step === 'quota'         && `🔒 ${t('tryon_quota_reached')}`}
            </Text>
            {usage.limit > 0 && step !== 'loading' && (
              <Text style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                {t('tryon_attempts_this_month').replace('{used}', String(usage.count)).replace('{limit}', String(usage.limit))}
              </Text>
            )}
          </View>
          {(step === 'mannequin' || step === 'capture-intro') && (
            <TouchableOpacity
              onPress={() => setStep('pieces')}
              style={{ paddingHorizontal: 12, paddingVertical: 10, marginRight: 4 }}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Text style={{ fontSize: 13, color: C.goldDark, fontWeight: '600' }}>{t('back_arrow')}</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            onPress={onClose}
            style={{ width: 44, height: 44, alignItems: 'center', justifyContent: 'center' }}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          >
            <Text style={{ fontSize: 28, color: C.textMuted, fontWeight: '300' }}>×</Text>
          </TouchableOpacity>
        </View>

        {/* ════ STEP 1 : Sélection des pièces à essayer ════ */}
        {step === 'pieces' && (
          <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 100 }}>
            <Text style={{ fontSize: 13, color: C.textMuted, marginBottom: 16, lineHeight: 18 }}>
              {t('tryon_check_pieces_intro')}
              {wearableItems.length > MAX_GARMENTS ? ` (${t('tryon_max_suffix').replace('{max}', String(MAX_GARMENTS))})` : ''}.
            </Text>

            {wearableItems.length === 0 ? (
              <View style={{ padding: 24, alignItems: 'center' }}>
                <Text style={{ fontSize: 13, color: C.textMuted, textAlign: 'center' }}>
                  {t('tryon_no_wearable_in_outfit')}{'\n'}
                  {t('tryon_no_wearable_hint')}
                </Text>
              </View>
            ) : (
              <View style={{ gap: 10 }}>
                {wearableItems.map((item, idx) => {
                  const key = itemKey(item, idx);
                  const isSelected = selectedItemKeys.has(key);
                  return (
                    <TouchableOpacity
                      key={key}
                      onPress={() => toggleItem(key)}
                      activeOpacity={0.85}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        backgroundColor: C.card,
                        borderRadius: 10,
                        borderWidth: 2,
                        borderColor: isSelected ? C.gold : C.border,
                        padding: 8,
                      }}>
                      {/* Image */}
                      <Image
                        source={{ uri: safeUri(item.image) }}
                        style={{ width: 60, height: 80, borderRadius: 6, resizeMode: 'cover' }}
                      />
                      {/* Infos */}
                      <View style={{ flex: 1, marginLeft: 12 }}>
                        <Text style={{ fontSize: 11, color: C.textFaint, marginBottom: 2 }}>
                          {tCat(item.category) || '—'}
                        </Text>
                        <Text numberOfLines={2} style={{ fontSize: 13, fontWeight: '600', color: C.text }}>
                          {item.title || item.brand || t('item_fallback')}
                        </Text>
                        {item.priceEur && (
                          <Text style={{ fontSize: 12, fontWeight: '700', color: C.goldDark, marginTop: 2 }}>
                            {Number(item.priceEur).toFixed(2)}€
                          </Text>
                        )}
                      </View>
                      {/* Checkbox */}
                      <View style={{
                        width: 28, height: 28, borderRadius: 14,
                        borderWidth: 2,
                        borderColor: isSelected ? C.gold : C.border,
                        backgroundColor: isSelected ? C.gold : 'transparent',
                        alignItems: 'center', justifyContent: 'center',
                        marginLeft: 8,
                      }}>
                        {isSelected && (
                          <Text style={{ color: '#fff', fontSize: 16, fontWeight: '700' }}>✓</Text>
                        )}
                      </View>
                    </TouchableOpacity>
                  );
                })}
              </View>
            )}

            {/* Articles non-portables (info seule) */}
            {nonWearableItems.length > 0 && (
              <View style={{ marginTop: 24 }}>
                <Text style={{ fontSize: 11, color: C.textFaint, marginBottom: 8 }}>
                  {t('not_tryable_label')}
                </Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  {nonWearableItems.map((item, idx) => (
                    <View
                      key={`nw-${idx}`}
                      style={{
                        paddingHorizontal: 10, paddingVertical: 4,
                        backgroundColor: '#F5F5F5', borderRadius: 12,
                      }}>
                      <Text style={{ fontSize: 10, color: C.textMuted }}>
                        {tCat(item.category) || ''}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            )}
          </ScrollView>
        )}

        {/* Footer "Continuer" — fixe en bas pour step 'pieces' */}
        {step === 'pieces' && wearableItems.length > 0 && (
          <View style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            padding: 16, backgroundColor: C.bg,
            borderTopWidth: 1, borderTopColor: C.border,
          }}>
            <TouchableOpacity
              onPress={goToMannequinStep}
              activeOpacity={0.85}
              disabled={selectedItemKeys.size === 0}
              style={{
                backgroundColor: selectedItemKeys.size === 0 ? '#CCC' : '#1A1A1A',
                paddingVertical: 14,
                borderRadius: 10,
                alignItems: 'center',
                borderWidth: 1,
                borderColor: selectedItemKeys.size === 0 ? '#CCC' : C.gold,
              }}>
              <Text style={{ fontSize: 14, fontWeight: '700', color: selectedItemKeys.size === 0 ? '#888' : C.gold, letterSpacing: 0.5 }}>
                {t('continue_btn')} ({selectedItemKeys.size}/{MAX_GARMENTS}) →
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {/* ════ STEP 1bis : Capture photo perso (première utilisation) ════ */}
        {step === 'capture-intro' && (
          <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
            {error && (
              <View style={{ backgroundColor: '#FFEBEE', padding: 12, borderRadius: 8, marginBottom: 16 }}>
                <Text style={{ color: C.red, fontSize: 13 }}>⚠️ {error}</Text>
              </View>
            )}

            <Text style={{ fontSize: 15, fontWeight: '700', color: C.text, marginBottom: 8 }}>
              {t('create_personal_mannequin_title')}
            </Text>
            <Text style={{ fontSize: 13, color: C.textMuted, lineHeight: 20, marginBottom: 16 }}>
              {t('create_personal_mannequin_desc')}
            </Text>

            <View style={{
              backgroundColor: C.card, borderRadius: 10, borderWidth: 1, borderColor: C.border,
              padding: 14, marginBottom: 16,
            }}>
              <Text style={{ fontSize: 12, fontWeight: '700', color: C.text, marginBottom: 8 }}>
                {t('photo_tips_title')}
              </Text>
              <Text style={{ fontSize: 12, color: C.textMuted, lineHeight: 18 }}>
                {`• ${t('photo_tip_full_body')}\n`}
                {`• ${t('photo_tip_stand_straight')}\n`}
                {`• ${t('photo_tip_plain_bg')}\n`}
                {`• ${t('photo_tip_fitted_clothes')}\n`}
                {`• ${t('photo_tip_one_person')}`}
              </Text>
            </View>

            {/* v1.12.940 — Apple 1.1 : avis 18+ */}
            <View style={{ backgroundColor: '#FFF4E5', borderRadius: 10, borderWidth: 1, borderColor: '#FFD8A8', padding: 12, marginBottom: 14, flexDirection: 'row', alignItems: 'center' }}>
              <Text style={{ fontSize: 18, marginRight: 8 }}>🔞</Text>
              <Text style={{ flex: 1, fontSize: 12, color: '#8A5A00', lineHeight: 17, fontWeight: '600' }}>
                {lang === 'en'
                  ? `Personal Try-On is reserved for users aged ${MIN_AGE_PERSONAL_TRYON} and over. Use your own photo only.`
                  : `Le Try-On sur photo personnelle est réservé aux personnes de ${MIN_AGE_PERSONAL_TRYON} ans et plus. Utilisez uniquement votre propre photo.`}
              </Text>
            </View>

            {/* v1.12.940 — Apple 1.1 : consentement 3 points */}
            {[
              { key: 'c18',   state: consent18,      set: setConsent18,      label: lang === 'en' ? `I am ${MIN_AGE_PERSONAL_TRYON} years old or older` : `J'ai ${MIN_AGE_PERSONAL_TRYON} ans ou plus` },
              { key: 'cself', state: consentSelf,    set: setConsentSelf,    label: lang === 'en' ? 'This photo is of myself, not someone else' : "Cette photo est une photo de moi, pas de quelqu'un d'autre" },
              { key: 'cmin',  state: consentNoMinor, set: setConsentNoMinor, label: lang === 'en' ? 'There are no minors in this photo' : 'Aucun mineur ne figure sur cette photo' },
            ].map(row => (
              <TouchableOpacity
                key={row.key}
                onPress={() => row.set(v => !v)}
                activeOpacity={0.85}
                style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12 }}
              >
                <View style={{
                  width: 22, height: 22, borderRadius: 4, marginRight: 10, marginTop: 1,
                  borderWidth: 2, borderColor: row.state ? C.gold : C.border,
                  backgroundColor: row.state ? C.gold : 'transparent',
                  alignItems: 'center', justifyContent: 'center',
                }}>
                  {row.state && <Text style={{ color: '#FFF', fontWeight: '900', fontSize: 14 }}>✓</Text>}
                </View>
                <Text style={{ flex: 1, fontSize: 12.5, color: C.text, lineHeight: 18 }}>
                  {row.label}
                </Text>
              </TouchableOpacity>
            ))}

            <Text style={{ fontSize: 11, color: C.textMuted, lineHeight: 16, marginTop: 4, marginBottom: 6 }}>
              {t('photo_storage_consent')}
            </Text>
            {/* v1.12.940 — Apple 1.1 : lien Community Guidelines depuis le consentement */}
            <TouchableOpacity onPress={() => openLegalUrl('community', lang)} activeOpacity={0.7} style={{ marginBottom: 16 }}>
              <Text style={{ fontSize: 11, color: C.goldDark, textDecorationLine: 'underline' }}>
                {lang === 'en' ? 'Read our Community Guidelines' : 'Lire nos règles communautaires'}
              </Text>
            </TouchableOpacity>

            <View style={{ flexDirection: 'row', gap: 10 }}>
              <TouchableOpacity
                onPress={() => handleCaptureMannequin(true)}
                disabled={!consentChecked}
                activeOpacity={0.85}
                style={{
                  flex: 1, paddingVertical: 14, borderRadius: 10, alignItems: 'center',
                  backgroundColor: consentChecked ? C.gold : C.border,
                }}
              >
                <Text style={{ fontSize: 14, fontWeight: '700', color: consentChecked ? '#FFF' : C.textFaint }}>
                  {`📷 ${t('take_a_photo')}`}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => handleCaptureMannequin(false)}
                disabled={!consentChecked}
                activeOpacity={0.85}
                style={{
                  flex: 1, paddingVertical: 14, borderRadius: 10, alignItems: 'center',
                  borderWidth: 2, borderColor: consentChecked ? C.gold : C.border,
                }}
              >
                <Text style={{ fontSize: 14, fontWeight: '700', color: consentChecked ? C.goldDark : C.textFaint }}>
                  {`🖼 ${t('choose_from_gallery')}`}
                </Text>
              </TouchableOpacity>
            </View>

            {/* v1.12.188 — Lien d'échappement : permet d'aller direct au choix
                des mannequins standards si l'user ne veut pas (ou ne peut pas)
                uploader sa photo perso (rate-limit, pas de consentement, etc.) */}
            <TouchableOpacity
              onPress={() => { setError(null); setStep('mannequin'); }}
              activeOpacity={0.7}
              style={{ marginTop: 24, alignSelf: 'center', paddingVertical: 8 }}
            >
              <Text style={{ fontSize: 13, color: C.textMuted, textDecorationLine: 'underline' }}>
                {t('continue_without_my_photo')}
              </Text>
            </TouchableOpacity>
          </ScrollView>
        )}

        {/* ════ STEP 1ter : Upload en cours ════ */}
        {step === 'uploading' && (
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 }}>
            <ActivityIndicator size="large" color={C.gold} />
            <Text style={{ fontSize: 16, fontWeight: '600', color: C.text, marginTop: 24 }}>
              {t('uploading_your_photo')}
            </Text>
            <Text style={{ fontSize: 12, color: C.textMuted, marginTop: 8, textAlign: 'center' }}>
              {t('secured_and_encrypted')}
            </Text>
          </View>
        )}

        {/* ════ STEP 2 : Sélection mannequin ════ */}
        {step === 'mannequin' && (
          <ScrollView contentContainerStyle={{ padding: 16, alignItems: isWeb ? 'center' : 'stretch' }}>
            {error && (
              <View style={{ width: '100%', maxWidth: contentW, backgroundColor: '#FFEBEE', padding: 12, borderRadius: 8, marginBottom: 16 }}>
                <Text style={{ color: C.red, fontSize: 13 }}>⚠️ {error}</Text>
              </View>
            )}

            <Text style={{ width: '100%', maxWidth: contentW, fontSize: 13, color: C.textMuted, marginBottom: 16, lineHeight: 18 }}>
              {t('tryon_choose_mannequin_intro')}
            </Text>

            {/* v1.12.84 — UI provider supprimée. Vertex est utilisé automatiquement
                à la sélection du mannequin. Plus de bouton de confirmation.
                v1.12.530 — grid responsif: contenu centré max 380px sur web */}

            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, width: '100%', maxWidth: contentW, justifyContent: 'flex-start' }}>
              {filteredMannequins.map(m => (
                // v1.12.185 : restructure — Wrapper View neutre. Le bouton ⋯ est
                // sibling du TouchableOpacity principal (et NON un enfant) pour
                // ne pas déclencher handleTryOn quand l'user clique le menu.
                <View
                  key={m.id}
                  style={{
                    width: mannequinCardW,
                    aspectRatio: 0.7,
                    backgroundColor: C.card,
                    borderRadius: 12,
                    overflow: 'hidden',
                    borderWidth: m.isPersonal ? 2 : 1,
                    borderColor: m.isPersonal ? C.gold : C.border,
                    position: 'relative',
                  }}>
                  <TouchableOpacity
                    onPress={() => handleTryOn(m)}
                    activeOpacity={0.85}
                    style={{ flex: 1 }}>
                    <Image
                      source={{ uri: safeUri(m.image) }}
                      style={{ width: '100%', height: '85%', resizeMode: 'cover' }}
                    />
                    <View style={{ padding: 8 }}>
                      <Text style={{ fontSize: 11, color: C.textFaint }}>
                        {tBody(m.bodyType)}{m.hair ? ` · ${tHair(m.hair)}` : ''}
                      </Text>
                    </View>
                  </TouchableOpacity>
                  {m.isPersonal && (
                    <View
                      pointerEvents="box-none"
                      style={{
                        position: 'absolute', top: 6, left: 6,
                        backgroundColor: C.gold, paddingHorizontal: 8, paddingVertical: 3,
                        borderRadius: 4,
                      }}>
                      <Text style={{ fontSize: 9, fontWeight: '800', color: '#FFF', letterSpacing: 0.5 }}>
                        {`✨ ${t('my_mannequin_caps')}`}
                      </Text>
                    </View>
                  )}
                  {m.isPersonal && (
                    <TouchableOpacity
                      onPress={handlePersonalMannequinAction}
                      activeOpacity={0.7}
                      hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                      style={{
                        position: 'absolute', top: 6, right: 6,
                        width: 32, height: 32, borderRadius: 16,
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        alignItems: 'center', justifyContent: 'center',
                        zIndex: 10,
                      }}>
                      <Text style={{ fontSize: 18, color: '#FFF', fontWeight: '700', lineHeight: 18 }}>⋯</Text>
                    </TouchableOpacity>
                  )}
                </View>
              ))}
            </View>
          </ScrollView>
        )}

        {/* ════ STEP 3 : Loader (avec stage de polling async) ════ */}
        {step === 'loading' && (
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 }}>
            <ActivityIndicator size="large" color={C.gold} />
            <Text style={{ fontSize: 16, fontWeight: '600', color: C.text, marginTop: 24 }}>
              {loadingStage === 'pending' && t('loading_stage_pending')}
              {loadingStage === 'created' && t('loading_stage_created')}
              {loadingStage === 'processing' && t('loading_stage_processing')}
              {loadingStage === 'completed' && t('loading_stage_completed')}
              {!['pending','created','processing','completed'].includes(loadingStage) && t('loading_stage_processing')}
            </Text>
            <Text style={{ fontSize: 12, color: C.textMuted, marginTop: 8, textAlign: 'center' }}>
              {t('tryon_ai_dressing').replace('{count}', String(selectedItemKeys.size))}{'\n'}
              {t('tryon_wait_polling')}
            </Text>
            {selectedMannequin && (
              <Image
                source={{ uri: safeUri(selectedMannequin.image) }}
                style={{ width: 80, height: 110, borderRadius: 8, marginTop: 24, opacity: 0.6 }}
                resizeMode="cover"
              />
            )}
          </View>
        )}

        {/* ════ STEP 4 : Résultat ════ */}
        {step === 'result' && resultUrl && (
          <ScrollView contentContainerStyle={{ padding: 16, alignItems: isWeb ? 'center' : 'stretch' }}>
            <View style={{
              width: '100%', maxWidth: resultMaxW || undefined,
              backgroundColor: C.card, borderRadius: 16, overflow: 'hidden',
              borderWidth: 1, borderColor: C.border, marginBottom: 16,
              position: 'relative',
              // Shadow subtle web
              ...(isWeb ? { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' } : {}),
            }}>
              {/* v1.12.533 — Tap pour zoom plein écran (lightbox) */}
              <TouchableOpacity
                activeOpacity={0.95}
                onPress={() => setZoomVisible(true)}>
                <Image source={{ uri: safeUri(resultUrl) }} style={{ width: '100%', aspectRatio: 0.7 }} resizeMode="cover" />
                <View style={{
                  position: 'absolute', bottom: 8, right: 8,
                  backgroundColor: 'rgba(0,0,0,0.55)',
                  paddingHorizontal: 9, paddingVertical: 4, borderRadius: 12,
                  flexDirection: 'row', alignItems: 'center', gap: 4,
                }}>
                  <Text style={{ fontSize: 11, color: '#FFF' }}>🔍</Text>
                  <Text style={{ fontSize: 10, color: '#FFF', fontWeight: '600', letterSpacing: 0.3 }}>{t('zoom') || 'Zoom'}</Text>
                </View>
              </TouchableOpacity>
              {/* v1.12.940 — Apple 1.1 : bouton Signaler le contenu */}
              <TouchableOpacity
                onPress={() => setShowReport(true)}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                style={{
                  position: 'absolute', top: 8, right: 8,
                  backgroundColor: 'rgba(0,0,0,0.55)',
                  paddingHorizontal: 9, paddingVertical: 5, borderRadius: 12,
                  flexDirection: 'row', alignItems: 'center', gap: 4,
                }}>
                <Text style={{ fontSize: 11, color: '#FFF' }}>🚩</Text>
                <Text style={{ fontSize: 10, color: '#FFF', fontWeight: '600', letterSpacing: 0.3 }}>
                  {lang === 'en' ? 'Report' : 'Signaler'}
                </Text>
              </TouchableOpacity>
              {/* v1.12.807 — Badge "Generated by {provider}" supprime sur demande user */}
            </View>

            {/* Articles de cette tenue (cliquables → détail produit) */}
            {outfit?.items?.length > 0 && (
              <View style={{ width: '100%', maxWidth: resultMaxW || undefined, marginBottom: 16 }}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: C.text, marginBottom: 8 }}>
                  {t('outfit_items_label')}
                </Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <View style={{ flexDirection: 'row', gap: 8 }}>
                    {outfit.items.map((item, idx) => (
                      <TouchableOpacity
                        key={`${item.id || idx}-${idx}`}
                        onPress={() => {
                          onClose?.();
                          onProductPress?.(item);
                        }}
                        activeOpacity={0.8}
                        style={{
                          width: 80,
                          backgroundColor: C.card,
                          borderRadius: 8,
                          overflow: 'hidden',
                          borderWidth: 1,
                          borderColor: C.border,
                        }}>
                        {item.image && (
                          <Image
                            source={{ uri: safeUri(item.image) }}
                            style={{ width: 80, height: 100, resizeMode: 'cover' }}
                          />
                        )}
                        <View style={{ padding: 4 }}>
                          <Text numberOfLines={1} style={{ fontSize: 9, color: C.textFaint, textAlign: 'center' }}>
                            {tCat(item.category) || ''}
                          </Text>
                          {item.priceEur && (
                            <Text style={{ fontSize: 10, fontWeight: '700', color: C.goldDark, textAlign: 'center' }}>
                              {Number(item.priceEur).toFixed(0)}€
                            </Text>
                          )}
                        </View>
                      </TouchableOpacity>
                    ))}
                  </View>
                </ScrollView>
              </View>
            )}

            {/* v1.12.530 — Boutons résultat dans le thème de l'app : primary gold + secondary border, pill rounded */}
            <View style={{ width: '100%', maxWidth: resultMaxW || undefined, flexDirection: 'row', gap: 10, marginBottom: 10 }}>
              <TouchableOpacity
                onPress={handleShare}
                activeOpacity={0.85}
                style={{
                  flex: 1, backgroundColor: C.goldDark || C.gold, paddingVertical: 14,
                  borderRadius: 24, alignItems: 'center', justifyContent: 'center',
                  ...(isWeb ? { boxShadow: '0 2px 8px rgba(212,175,114,0.35)' } : {}),
                }}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: '#fff', letterSpacing: 0.8 }}>{`📤  ${t('share').toUpperCase()}`}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setStep('mannequin')}
                activeOpacity={0.85}
                style={{
                  flex: 1, backgroundColor: '#FFF', paddingVertical: 14,
                  borderRadius: 24, borderWidth: 1.5, borderColor: C.gold,
                  alignItems: 'center', justifyContent: 'center',
                }}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: C.goldDark || C.gold, letterSpacing: 0.8 }}>{`🔄  ${t('other_mannequin').toUpperCase()}`}</Text>
              </TouchableOpacity>
            </View>

            <TouchableOpacity
              onPress={() => setStep('pieces')}
              activeOpacity={0.85}
              style={{
                width: '100%', maxWidth: resultMaxW || undefined,
                paddingVertical: 12, alignItems: 'center', justifyContent: 'center',
                marginBottom: 6,
                borderRadius: 24, backgroundColor: '#FAF7F2', borderWidth: 1, borderColor: '#EFE7D6',
              }}>
              <Text style={{ fontSize: 13, color: C.goldDark || C.gold, fontWeight: '600', letterSpacing: 0.3 }}>
                {`✏️  ${t('modify_pieces')}`}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity onPress={onClose} activeOpacity={0.7} style={{ paddingVertical: 10, alignItems: 'center' }}>
              <Text style={{ fontSize: 12, color: C.textMuted, textDecorationLine: 'underline' }}>{t('close')}</Text>
            </TouchableOpacity>
          </ScrollView>
        )}

        {/* ════ STEP 5 : Quota atteint (v1.10.17 : message contextuel + date reset) ════ */}
        {step === 'quota' && (() => {
          // Helper : formater la date de reset (locale courante)
          const formatResetDate = (iso) => {
            if (!iso) return null;
            try {
              const d = new Date(iso);
              const localeMap = { fr: 'fr-FR', en: 'en-US', es: 'es-ES', it: 'it-IT', de: 'de-DE', pt: 'pt-PT', ar: 'ar', ru: 'ru-RU', tr: 'tr-TR' };
              const locale = localeMap[lang] || 'en-US';
              return d.toLocaleDateString(locale, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
              });
            } catch { return null; }
          };
          const resetDateLabel = formatResetDate(usage.nextResetDate);
          const tierLabels = { free: 'Free', basic: 'Basic', premium: 'Premium', gold: 'Gold' };
          const currentTierLabel = tierLabels[usage.tier] || t('your_plan');

          // Texte adapté au tier
          let mainTitle, mainDesc, ctaLabel;
          if (usage.tier === 'gold') {
            // Gold = max, pas d'upgrade possible
            mainTitle = t('quota_gold_reached');
            mainDesc = t('quota_used_all').replace('{limit}', String(usage.limit));
            ctaLabel = null; // pas de CTA upgrade
          } else if (usage.isPaidTier) {
            // Basic / Premium → upgrade vers le tier suivant
            const nextTier = usage.tier === 'basic' ? 'Premium' : 'Gold';
            mainTitle = t('quota_tier_reached').replace('{tier}', currentTierLabel);
            mainDesc = t('quota_used_this_month_upgrade')
              .replace('{limit}', String(usage.limit))
              .replace('{nextTier}', nextTier);
            ctaLabel = `✨ ${t('upgrade_to').replace('{tier}', nextTier)}`;
          } else {
            // v1.12.996 — Free : PAS de quota hebdo (supprimé). Les essais free
            // viennent du cadeau d'onboarding / packs add-on / abonnement.
            const _en = lang === 'en';
            mainTitle = _en ? 'No try-ons left' : "Plus d'essais disponibles";
            mainDesc = _en
              ? 'Virtual try-on and Flat-Lay aren\'t included in the free plan. Buy a Try-On pack, or pick a Basic, Premium or Gold plan to get try-ons included.'
              : "L'essayage virtuel et le Flat-Lay ne sont pas inclus dans le plan gratuit. Achetez un pack d'essais, ou choisissez un abonnement Basic, Premium ou Gold pour en bénéficier.";
            ctaLabel = `✨ ${_en ? 'See options' : 'Voir les options'}`;
          }

          return (
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 }}>
              <Text style={{ fontSize: 48, marginBottom: 16 }}>🔒</Text>
              <Text style={{ fontSize: 20, fontWeight: '700', color: C.text, textAlign: 'center', marginBottom: 8 }}>
                {mainTitle}
              </Text>
              <Text style={{ fontSize: 14, color: C.textMuted, textAlign: 'center', lineHeight: 20, marginBottom: 16 }}>
                {mainDesc}
              </Text>

              {/* Compteur visuel */}
              <View style={{
                backgroundColor: C.bg2 || '#F5F5F5',
                paddingHorizontal: 20, paddingVertical: 12, borderRadius: 8,
                marginBottom: 20,
              }}>
                <Text style={{ fontSize: 24, fontWeight: '800', color: C.text, textAlign: 'center' }}>
                  {usage.count}/{usage.limit}
                </Text>
                <Text style={{ fontSize: 11, color: C.textMuted, textAlign: 'center', marginTop: 2 }}>
                  {t('tryons_used')}
                </Text>
              </View>

              {/* Date de reset — JAMAIS pour les free (pas de cycle hebdo/mensuel) */}
              {resetDateLabel && usage.tier !== 'free' && (
                <View style={{
                  flexDirection: 'row', alignItems: 'center', gap: 6,
                  backgroundColor: '#FFF7E6', paddingHorizontal: 14, paddingVertical: 8,
                  borderRadius: 8, marginBottom: 24,
                }}>
                  <Text style={{ fontSize: 14 }}>🗓️</Text>
                  <Text style={{ fontSize: 12, color: '#8B6F2E', fontWeight: '600' }}>
                    {t('renewal_label').replace('{date}', resetDateLabel)}
                  </Text>
                </View>
              )}

              {ctaLabel && (
                <TouchableOpacity
                  onPress={() => {
                    // v1.12.994 — Ouvrir la VRAIE popup paywall (UpgradeModal feature=tryon :
                    // add-ons Try-On + "Or upgrade → MyTenue Premium") au lieu de naviguer
                    // vers Profil. Même mécanisme que le path "quota au montage".
                    onClose?.();
                    try {
                      DeviceEventEmitter.emit('tryon_limit_reached', {
                        tier:          usage.tier,
                        limit:         usage.limit,
                        current:       usage.count,
                        isPaidTier:    usage.isPaidTier,
                        nextResetDate: usage.nextResetDate,
                      });
                    } catch (e) { onUpgradePress?.(); }
                  }}
                  activeOpacity={0.85}
                  style={{ backgroundColor: C.gold, paddingVertical: 16, paddingHorizontal: 32, borderRadius: 10 }}>
                  <Text style={{ fontSize: 14, fontWeight: '700', color: '#fff', letterSpacing: 0.5 }}>
                    {ctaLabel}
                  </Text>
                </TouchableOpacity>
              )}

              <TouchableOpacity onPress={onClose} style={{ marginTop: 16, paddingVertical: 8 }}>
                <Text style={{ fontSize: 13, color: C.textMuted }}>{t('close')}</Text>
              </TouchableOpacity>
            </View>
          );
        })()}
      </View>

      {/* v1.12.627 — Lightbox plein écran. Taille de l'image bornée au
           viewport (ratio 0.7 préservé) pour que le bouton ✕ reste visible
           en haut. zIndex bouton > image pour éviter qu'elle ne le masque. */}
      {zoomVisible && resultUrl && (() => {
        // Calcul taille image : ratio 0.7 (portrait) + ne dépasse pas viewport
        // Sur web, contrainte additionnelle : max 500px de large pour rester
        // raisonnable sur grand écran.
        const maxW = Platform.OS === 'web' ? Math.min(winW * 0.9, 500) : winW;
        const maxH = (winH || 800) * 0.88; // 88% du viewport (laisse place pour le bouton)
        const imgWidth = Math.min(maxW, maxH * 0.7);
        const imgHeight = imgWidth / 0.7;
        return (
          <View
            style={{
              position: Platform.OS === 'web' ? 'fixed' : 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: '#000',
              zIndex: 999999,
              elevation: 999,
            }}
            pointerEvents="auto"
          >
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}
              maximumZoomScale={4}
              minimumZoomScale={1}
              bouncesZoom
              showsHorizontalScrollIndicator={false}
              showsVerticalScrollIndicator={false}
              pinchGestureEnabled
              centerContent
            >
              <Image
                source={{ uri: safeUri(resultUrl) }}
                style={{ width: imgWidth, height: imgHeight }}
                resizeMode="contain"
              />
            </ScrollView>
            <TouchableOpacity
              onPress={closeZoom}
              activeOpacity={0.7}
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              style={{
                // v1.12.994 — descendre sous la safe area (notch/dynamic island)
                // pour que le bouton fermer ne touche pas le haut de l'écran.
                position: 'absolute', top: Math.max(insets.top, 20) + 8, right: 16,
                width: 48, height: 48, borderRadius: 24,
                backgroundColor: 'rgba(0,0,0,0.6)',
                borderWidth: 1, borderColor: 'rgba(255,255,255,0.3)',
                alignItems: 'center', justifyContent: 'center',
                zIndex: 1000000, // au-dessus de l'image et du scroll
              }}>
              <Text style={{ fontSize: 24, color: '#FFF', fontWeight: '300' }}>✕</Text>
            </TouchableOpacity>
          </View>
        );
      })()}
    </AppModal>
    {/* v1.12.940 — Apple 1.1 : signalement de contenu (sibling, hors AppModal) */}
    <ReportContentModal
      visible={showReport}
      onClose={() => setShowReport(false)}
      resultUrl={resultUrl}
      context="tryon"
    />
    </>
  );
}
